import type { ImportDraft } from './types';

/** Per-row validation outcome. Errors block the row from committing; warnings
 *  are advisory (the row still imports). */
export interface RowIssues {
  errors: string[];
  warnings: string[];
}

const EMPTY: RowIssues = { errors: [], warnings: [] };

/**
 * Codes (lowercased) that appear on more than one row in the batch. The
 * second+ occurrence will 409 at commit and be skipped, so we surface it as
 * a warning up front. Blank codes are ignored (they auto-generate, so no
 * collision).
 */
export function findInFileDuplicateCodes(drafts: ImportDraft[]): Set<string> {
  const counts = new Map<string, number>();
  for (const d of drafts) {
    const c = d.code.trim().toLowerCase();
    if (!c) continue;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  const dups = new Set<string>();
  for (const [code, n] of counts) if (n > 1) dups.add(code);
  return dups;
}

/** Validate a single draft against the required-field rules + light sanity
 *  checks. `dupCodes` comes from {@link findInFileDuplicateCodes}. */
export function validateDraft(d: ImportDraft, dupCodes: Set<string>): RowIssues {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!d.name.trim()) errors.push('Name is required');
  if (!d.fullAddress.trim()) errors.push('Address is required');
  if (!d.country.trim()) errors.push('Country is required');

  if (d.country.trim() && d.country.trim().length !== 2) {
    warnings.push('Country should be a 2-letter ISO code (IE / GB / XI)');
  }
  if (d.code.trim() && dupCodes.has(d.code.trim().toLowerCase())) {
    warnings.push('Duplicate code in file — the later row will be skipped on import');
  }
  if (d.latitude != null && (d.latitude < -90 || d.latitude > 90)) {
    warnings.push('Latitude is out of range');
  }
  if (d.longitude != null && (d.longitude < -180 || d.longitude > 180)) {
    warnings.push('Longitude is out of range');
  }
  if ((d.latitude == null) !== (d.longitude == null)) {
    warnings.push('Latitude and longitude should be set together');
  }

  return { errors, warnings };
}

/** Validate every draft → rowRef → issues. */
export function validateAll(drafts: ImportDraft[]): Map<string, RowIssues> {
  const dupCodes = findInFileDuplicateCodes(drafts);
  const out = new Map<string, RowIssues>();
  for (const d of drafts) out.set(d.rowRef, validateDraft(d, dupCodes));
  return out;
}

/** A row is committable when it has no blocking errors. */
export function isCommittable(issues: RowIssues | undefined): boolean {
  return (issues ?? EMPTY).errors.length === 0;
}
